import { rest } from "msw";
import type { RestHandler } from "msw";
import config from "../../../config";
import { createUrl } from "../../protect-api";
import { apiVersion } from "../../../shared/constants/common.constant";
import type {
  SanctionBody,
  SettingsValues,
} from "../../../shared/types/common.type";
import type {
  PreferencesResponse,
  UpdatePreferencesPayload,
} from "../../../modules/global-settings/settings.types";
import type { EmailSubscriptionUpdate } from "../settings.types";
import { MOCK_TEAM_IDS } from "../../teams/mocks/teams-mock-api";

const HTTP_OK = 200;
const HTTPS_ERROR = 404;

const defaultSubscribedModules = [
  "profileAssignment",
  "roleAssignment",
  "batchUpload",
  "permissionChanges",
  "criteriaOverride",
  "amlAlerts",
  "kycAlerts",
];

const defaultUnsubscribedModules = ["searchAssignment"];

interface StoredEmailSubscription {
  subscribed: string[];
  unsubscribed: string[];
}

const emailSubscriptionsStore = new Map<string, StoredEmailSubscription>();

const getSubscriptionKey = (teamId: string): string => teamId;

const getOrCreateSubscription = (teamId: string): StoredEmailSubscription => {
  const key = getSubscriptionKey(teamId);
  const existing = emailSubscriptionsStore.get(key);

  if (existing) {
    return existing;
  }

  const subscription: StoredEmailSubscription = {
    subscribed: [...defaultSubscribedModules],
    unsubscribed: [...defaultUnsubscribedModules],
  };

  emailSubscriptionsStore.set(key, subscription);
  return subscription;
};

const applySubscriptionUpdates = (
  subscription: StoredEmailSubscription,
  updates: EmailSubscriptionUpdate[]
): void => {
  updates.forEach(({ module, isSubscribed }) => {
    if (isSubscribed) {
      if (!subscription.subscribed.includes(module)) {
        subscription.subscribed.push(module);
      }
      subscription.unsubscribed = subscription.unsubscribed.filter(
        (item) => item !== module
      );
      return;
    }

    if (!subscription.unsubscribed.includes(module)) {
      subscription.unsubscribed.push(module);
    }
    subscription.subscribed = subscription.subscribed.filter(
      (item) => item !== module
    );
  });
};

export const resetMockEmailSubscriptions = (): void => {
  emailSubscriptionsStore.clear();
};

export const mockGetSanctionBodies: RestHandler = rest.get(
  createUrl(`${config.COMPLIANCE_API_URL}/${apiVersion}/lookup/sanctionBodies`),
  async (_, res, ctx) => {
    const sanctionBodies: SanctionBody[] = [
      {
        id: 1,
        name: "Asian Development Bank",
        region: "eas",
      },
      {
        id: 2,
        name: "Australian Department of Foreign Affairs and Trade",
        region: "eas",
      },
      {
        id: 3,
        name: "Australian Government National Security",
        region: "eas",
      },
      {
        id: 4,
        name: "Australian National Security",
        region: "eas",
      },
      {
        id: 5,
        name: "Azerbaijan Financial Markets Supervision Chamber - Financial Monitoring Service",
        region: "ecs",
      },
      {
        id: 6,
        name: "Bahrain Ministry of Foreign Affairs",
        region: "mea",
      },
      {
        id: 7,
        name: "Belgian Federal Public Service Finance",
        region: "ecs",
      },
      {
        id: 8,
        name: "Canadian Government Sanctions",
        region: "nac",
      },
      {
        id: 9,
        name: "Central Bank of Bangladesh",
        region: "sas",
      },
      {
        id: 10,
        name: "Central Bank of Egypt",
        region: "mea",
      },
    ];

    return res(ctx.status(HTTP_OK), ctx.json(sanctionBodies));
  }
);

export const mockGetPreferences = (
  preferencesMap: Record<string, PreferencesResponse>
): RestHandler =>
  rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/settings/preferences/aml`
    ),
    async (req, res, ctx) => {
      const searchType = req.url.searchParams.get("searchType");
      const searchActionType = req.url.searchParams.get("searchActionType");

      const key = `${searchType}_${searchActionType}`;
      const response = preferencesMap[key];

      if (!response) {
        return res(ctx.status(HTTPS_ERROR), ctx.json({ message: "Not found" }));
      }

      return res(ctx.status(HTTP_OK), ctx.json(response));
    }
  );

export const mockOptionsPreferences: RestHandler = rest.options(
  createUrl(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/settings/preferences/aml`
  ),
  (_req, res, ctx) =>
    res(
      ctx.status(HTTP_OK),
      ctx.set("Access-Control-Allow-Origin", "*"),
      ctx.set("Access-Control-Allow-Methods", "PUT, OPTIONS"),
      ctx.set("Access-Control-Allow-Headers", "Content-Type, Authorization")
    )
);

export const mockPutPreferences = (
  updatedPreferences: SettingsValues
): RestHandler =>
  rest.put(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/settings/preferences/aml`
    ),
    async (req, res, ctx) => {
      const payload = await req.json<UpdatePreferencesPayload>();

      if (!payload.searchType || !payload.sanctionBodies) {
        return res(
          ctx.status(HTTPS_ERROR),
          ctx.json({ message: "Missing required fields" })
        );
      }

      return res(ctx.status(HTTP_OK), ctx.json(updatedPreferences));
    }
  );

export const mockGetEmailSubscriptions = (): RestHandler =>
  rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/Settings/emailSubscriptions`
    ),
    async (req, res, ctx) => {
      const teamId =
        req.url.searchParams.getAll("teamIds")[0] ?? MOCK_TEAM_IDS.untagged;
      const userId = Number.parseInt(
        req.url.searchParams.getAll("userIds")[0] ?? "1",
        10
      );
      const subscription = getOrCreateSubscription(teamId);

      return res(
        ctx.status(HTTP_OK),
        ctx.json([
          {
            subscribed: [...subscription.subscribed],
            unsubscribed: [...subscription.unsubscribed],
            teamId,
            teamName: "Untagged/Default",
            userId,
            userName: "Mock User",
            modifiedAt: new Date().toISOString(),
          },
        ])
      );
    }
  );

export const mockPatchEmailSubscriptions = (): RestHandler =>
  rest.patch(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/Settings/emailSubscriptions`
    ),
    async (req, res, ctx) => {
      const teamId = req.headers.get("X-Team-Id") ?? MOCK_TEAM_IDS.untagged;
      const updates = await req.json<EmailSubscriptionUpdate[]>();
      const subscription = getOrCreateSubscription(teamId);

      applySubscriptionUpdates(subscription, updates);

      return res(
        ctx.status(HTTP_OK),
        ctx.json({
          subscribed: [...subscription.subscribed],
          unsubscribed: [...subscription.unsubscribed],
          createdAt: new Date().toISOString(),
          modifiedAt: new Date().toISOString(),
        })
      );
    }
  );
